import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Camera,
  Flame,
  TrendingUp,
  LogOut,
  Target,
  Award,
  Trophy,
  ChevronRight,
  Home,
  Gift,
  Users,
  Cloud,
  Layers,
  QrCode,
  Zap,
  User,
} from 'lucide-react';
import LogoAvatar from '@/components/LogoAvatar';
import OnboardingOverlay, { useOnboarding } from '@/components/OnboardingOverlay';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import { SCOUT_LEVEL_THRESHOLDS, getXpForNextLevel, SCOUT_POINT_VALUES } from '@tendedloop/shared';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { getBadgeIcon } from '@/utils/badgeIcons';
import { rankName } from '@/utils/rankName';
import { catalogText } from '@/utils/catalog';

interface MissionPreview {
  id: string;
  progress: number;
  target: number;
  status: string;
  expiresAt: string | null;
  template: { slug?: string; name: string; xpReward: number; type: string };
}

interface BadgePreview {
  id: string;
  slug: string;
  name: string;
  rarity: string;
  category: string;
  earnedAt: string;
}

interface RankPreview {
  rank: number | null;
  xpEarned: number;
  total: number;
}

interface CollectionPreview {
  id: string;
  tenantName: string;
  logoUrl: string | null;
}

interface NextGoalBadge {
  slug: string;
  name: string;
  rarity: string;
  category: string;
  criteriaType: string;
  criteriaThreshold: number;
  xpReward: number;
  progress: number;
  progressPct: number;
}

export default function ScoutHome() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { scout, isAuthenticated, checkAuth, logout } = useAuthStore();
  const { showOnboarding, dismissOnboarding } = useOnboarding();
  const [todayXp, setTodayXp] = useState<number | null>(null);
  const [missions, setMissions] = useState<MissionPreview[]>([]);
  const [recentBadges, setRecentBadges] = useState<BadgePreview[]>([]);
  const [rank, setRank] = useState<RankPreview | null>(null);
  const [collectionPreview, setCollectionPreview] = useState<CollectionPreview[]>([]);
  const [reserveStats, setReserveStats] = useState<{
    total: number;
    thriving: number;
    needsAttention: number;
    atRisk: number;
  } | null>(null);
  const [reserveAnimals, setReserveAnimals] = useState<
    Array<{ slug: string; name: string; thumbPath: string; healthState: string }>
  >([]);
  const [nextGoal, setNextGoal] = useState<NextGoalBadge | null>(null);
  const [dashboardError, setDashboardError] = useState(false);
  const { pendingCount } = useNetworkStatus();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    checkAuth();
    // Fetch all dashboard data in parallel
    Promise.allSettled([
      api
        .get<{ today: number; week: number; month: number }>('/scout/points/summary')
        .then((data) => setTodayXp(data.today)),
      api
        .get<MissionPreview[]>('/scout/missions')
        .then((data) => setMissions(data.filter((m) => m.status === 'ACTIVE' || m.status === 'COMPLETED').slice(0, 3))),
      api.get<BadgePreview[]>('/scout/badges/recent?limit=3').then((data) => setRecentBadges(data)),
      api.get<RankPreview>('/scout/leaderboard/rank?period=WEEKLY').then((data) => setRank(data)),
      api.get<CollectionPreview[]>('/scout/collection').then((data) => setCollectionPreview(data.slice(0, 4))),
      // Only fetch reserve data if globe is enabled (or not yet known)
      ...(scout?.globeEnabled === false
        ? []
        : [
            api
              .get<{
                species: Array<{ slug: string; name: string; thumbPath: string; healthState: string }>;
                stats: { total: number; thriving: number; needsAttention: number; atRisk: number };
              }>('/scout/globe/reserve?limit=5')
              .then((data) => {
                setReserveStats(data.stats);
                setReserveAnimals(data.species.slice(0, 5));
              }),
          ]),
      // Fetch all badges and find the closest unearned one
      api
        .get<
          Array<{
            slug: string;
            name: string;
            rarity: string;
            category: string;
            criteriaType: string;
            criteriaThreshold: number;
            xpReward: number;
            isSecret: boolean;
            earned: boolean;
          }>
        >('/scout/badges')
        .then((badges) => {
          if (!scout) return;
          const getCurrent = (ct: string): number => {
            switch (ct) {
              case 'TOTAL_SCANS':
                return scout.totalScans;
              case 'TOTAL_FEEDBACK':
                return scout.totalFeedback;
              case 'STREAK_DAYS':
                return Math.max(scout.currentStreak, scout.longestStreak);
              case 'TOTAL_ISSUES':
                return scout.totalIssues;
              case 'REFERRALS':
                return scout.totalReferrals;
              case 'COMPANY_REFERRALS':
                return scout.totalCompanyReferrals;
              default:
                return 0;
            }
          };
          const candidates = badges
            .filter((b) => !b.earned && !b.isSecret && b.criteriaThreshold > 0)
            .map((b) => {
              const progress = getCurrent(b.criteriaType);
              const progressPct = Math.min((progress / b.criteriaThreshold) * 100, 99);
              return { ...b, progress, progressPct };
            })
            .sort((a, b) => b.progressPct - a.progressPct);
          if (candidates[0]) setNextGoal(candidates[0]);
        }),
    ]).then((results) => {
      const allFailed = results.every((r) => r.status === 'rejected');
      if (allFailed) setDashboardError(true);
    });
  }, [isAuthenticated, navigate, checkAuth]);

  if (!scout) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const currentLevel = SCOUT_LEVEL_THRESHOLDS.find((l) => l.level === scout.level) ?? SCOUT_LEVEL_THRESHOLDS[0];
  const nextLevel = SCOUT_LEVEL_THRESHOLDS.find((l) => l.level === scout.level + 1);
  const xpToNext = getXpForNextLevel(scout.totalXp);
  const progressPercent = nextLevel
    ? ((scout.totalXp - currentLevel.xp) / (nextLevel.xp - currentLevel.xp)) * 100
    : 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white pb-24">
      <OnboardingOverlay visible={showOnboarding} onDismiss={dismissOnboarding} />

      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-section font-bold text-foreground truncate">
            {(() => {
              const hour = new Date().getHours();
              const key =
                hour < 12 ? 'home.greetingMorning' : hour < 18 ? 'home.greetingAfternoon' : 'home.greetingEvening';
              return t(key, { name: scout.name || scout.email.split('@')[0] });
            })()}
          </h1>
          <p className="text-small text-muted-foreground">{rankName(currentLevel.level, currentLevel.name)}</p>
        </div>
        <button
          onClick={logout}
          className="p-2 rounded-lg hover:bg-warm-faint text-muted-foreground"
          aria-label={t('home.logout')}
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Profile Card */}
      <div className="mx-4 p-4 rounded-2xl bg-gradient-to-br from-primary to-forest text-white shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold">
              {scout.level}
            </div>
            <div>
              <p className="font-semibold">{t('home.level', { level: scout.level })}</p>
              <p className="text-sm text-white/80">{scout.totalXp} XP</p>
            </div>
          </div>
          {/* Streak shown in hero card below */}
        </div>

        {/* XP Progress Bar */}
        <div>
          <div className="flex justify-between text-xs text-white/70 mb-1.5">
            <span>{t('home.levelProgress')}</span>
            <span>{Math.round(Math.min(progressPercent, 100))}%</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${Math.min(progressPercent, 100)}%`,
                background: !nextLevel
                  ? 'linear-gradient(90deg, #C49F38 0%, #E8C547 100%)'
                  : 'linear-gradient(90deg, rgba(255,255,255,0.8) 0%, #fff 100%)',
                boxShadow: !nextLevel ? '0 0 12px rgba(196,159,56,0.6)' : '0 0 8px rgba(255,255,255,0.4)',
              }}
            />
          </div>
          <p className="text-xs text-white/60 mt-1.5 text-center">
            {xpToNext !== null ? (
              <>{t('home.xpToNext', { xp: xpToNext, defaultValue: '{{xp}} XP to next' })}</>
            ) : (
              t('home.maxLevel')
            )}
          </p>
        </div>
      </div>

      {scout.totalScans === 0 ? (
        /* Welcome card for brand new Scouts */
        <div className="mx-4 mt-4">
          <button
            onClick={() => navigate('/scan')}
            className="w-full p-5 rounded-2xl bg-white border-2 border-dashed border-primary/30 hover:border-primary/60 hover:shadow-md transition-all active:scale-[0.98]"
          >
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-forest flex items-center justify-center shadow-md">
                <QrCode className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-small font-bold text-foreground mb-1">{t('home.welcomeTitle')}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{t('home.welcomeMessage')}</p>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/10">
                <Zap className="w-3.5 h-3.5 text-gold" />
                <span className="text-caption font-semibold text-gold">
                  {t('home.welcomeXpHint', { xp: SCOUT_POINT_VALUES.SCAN + SCOUT_POINT_VALUES.FIRST_SCAN_OF_DAY })}
                </span>
              </div>
              <span className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small">
                <Camera className="w-4 h-4" />
                {t('home.welcomeScanButton')}
              </span>
            </div>
          </button>
        </div>
      ) : (
        <>
          {/* Quick Stats - hidden for early scouts (1-5 scans) to avoid showing demotivating zeros */}
          {scout.totalScans > 5 && (
            <div className="grid grid-cols-3 gap-3 mx-4 mt-4">
              <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-warm-border">
                <p className="text-section font-bold text-foreground">{scout.totalScans}</p>
                <p className="text-caption text-muted-foreground">{t('home.scans')}</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-warm-border">
                <p className="text-section font-bold text-foreground">{scout.totalFeedback}</p>
                <p className="text-caption text-muted-foreground">{t('home.feedback')}</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-warm-border">
                <p className="text-section font-bold text-foreground">{scout.longestStreak}</p>
                <p className="text-caption text-muted-foreground">{t('home.bestStreak')}</p>
              </div>
            </div>
          )}

          {/* Streak + Today's XP combined hero */}
          <div className="mx-4 mt-4 flex gap-3">
            {/* Streak card */}
            <button
              onClick={() => navigate('/scan')}
              className={`flex-1 p-3 rounded-xl border shadow-sm text-center active:scale-[0.98] transition-all ${
                scout.currentStreak > 0 && (!todayXp || todayXp === 0)
                  ? 'bg-gold/10 border-gold/30 ring-1 ring-gold/20'
                  : 'bg-white border-warm-border'
              }`}
            >
              <Flame className={`w-5 h-5 mx-auto mb-1 ${scout.currentStreak > 0 ? 'text-gold' : 'text-warm-border'}`} />
              <p className="text-section font-bold text-foreground">{scout.currentStreak}</p>
              <p className="text-caption text-muted-foreground">{t('home.streak')}</p>
              {scout.currentStreak > 0 && (!todayXp || todayXp === 0) && (
                <p className="text-[10px] text-gold font-medium mt-1">{t('home.streakAtRiskShort')}</p>
              )}
              {todayXp !== null && todayXp > 0 && (
                <p className="text-[10px] text-forest font-medium mt-1">{t('home.streakSafe')}</p>
              )}
            </button>

            {/* Today's XP card */}
            <div className="flex-1 p-3 rounded-xl bg-white border border-warm-border shadow-sm text-center">
              <TrendingUp className="w-5 h-5 mx-auto mb-1 text-primary" />
              <p className="text-section font-bold text-foreground">{todayXp !== null ? todayXp : ', '}</p>
              <p className="text-caption text-muted-foreground">{t('home.todayXpLabel')}</p>
            </div>
          </div>
        </>
      )}

      {/* Next Goal */}
      {nextGoal && scout && scout.totalScans > 0 && (
        <button
          onClick={() => navigate('/badges')}
          className="mx-4 mt-4 p-4 rounded-xl bg-white border border-warm-border shadow-sm flex items-center gap-3 active:scale-[0.98] transition-all w-auto text-start"
        >
          {(() => {
            const GoalIcon = getBadgeIcon(nextGoal.slug, nextGoal.category);
            return (
              <div className="w-11 h-11 rounded-full bg-violet/10 flex items-center justify-center flex-shrink-0">
                <GoalIcon className="w-5 h-5 text-violet" />
              </div>
            );
          })()}
          <div className="flex-1 min-w-0">
            <p className="text-caption text-muted-foreground">{t('home.nextGoal')}</p>
            <p className="text-small font-semibold text-foreground truncate">
              {catalogText(nextGoal.slug, 'name', nextGoal.name)}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet rounded-full transition-all"
                  style={{ width: `${nextGoal.progressPct}%` }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {nextGoal.progress}/{nextGoal.criteriaThreshold}
              </span>
            </div>
          </div>
          <span className="text-caption font-semibold text-forest whitespace-nowrap">+{nextGoal.xpReward}</span>
        </button>
      )}

      {/* Dashboard error */}
      {dashboardError && (
        <div className="mx-4 mt-4 p-3 rounded-xl bg-critical/5 border border-critical/10">
          <p className="text-small text-critical text-center">{t('home.loadError')}</p>
        </div>
      )}

      {/* Missions Preview */}
      {missions.length > 0 && (
        <div className="mx-4 mt-4">
          <button onClick={() => navigate('/missions')} className="flex items-center justify-between w-full mb-2">
            <h3 className="text-small font-semibold text-foreground flex items-center gap-1.5">
              <Target className="w-4 h-4 text-gold" />
              {t('home.missions')}
            </h3>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="space-y-2">
            {missions.map((m) => (
              <div
                key={m.id}
                className={`p-3 rounded-xl border shadow-sm ${
                  m.status === 'COMPLETED' ? 'bg-primary/5 border-forest/20' : 'bg-white border-warm-border'
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-small font-medium text-foreground">
                    {catalogText(m.template.slug, 'name', m.template.name)}
                  </span>
                  <span className="text-caption font-semibold text-forest">+{m.template.xpReward}</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      m.status === 'COMPLETED' ? 'bg-primary' : 'bg-gold'
                    }`}
                    style={{ width: `${m.target > 0 ? Math.min((m.progress / m.target) * 100, 100) : 0}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-caption text-muted-foreground">
                    {m.progress}/{m.target}
                  </span>
                  {m.expiresAt &&
                    m.status === 'ACTIVE' &&
                    (() => {
                      const msLeft = Math.max(0, new Date(m.expiresAt).getTime() - Date.now());
                      const hoursLeft = Math.floor(msLeft / 3600000);
                      const minutesLeft = Math.floor((msLeft % 3600000) / 60000);
                      if (hoursLeft > 24) return null;
                      return (
                        <span className="text-[10px] text-gold">
                          {hoursLeft > 0
                            ? t('home.hoursLeft', { count: hoursLeft })
                            : t('home.minutesLeft', { count: minutesLeft })}
                        </span>
                      );
                    })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard Preview */}
      {rank && rank.rank && (
        <div className="mx-4 mt-4">
          <button onClick={() => navigate('/leaderboard')} className="flex items-center justify-between w-full mb-2">
            <h3 className="text-small font-semibold text-foreground flex items-center gap-1.5">
              <Trophy className="w-4 h-4 text-gold" />
              {t('home.leaderboard')}
            </h3>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="p-3 rounded-xl bg-gradient-to-r from-gold/5 to-gold/10 border border-gold/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-caption text-gold">{t('home.weeklyRank')}</p>
                <p className="text-section font-bold text-foreground">#{rank.rank}</p>
              </div>
              <div className="text-end">
                <p className="text-caption text-gold">{t('home.thisWeek')}</p>
                <p className="text-section font-semibold text-foreground">{rank.xpEarned} XP</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Badges */}
      {recentBadges.length > 0 && (
        <div className="mx-4 mt-4">
          <button onClick={() => navigate('/badges')} className="flex items-center justify-between w-full mb-2">
            <h3 className="text-small font-semibold text-foreground flex items-center gap-1.5">
              <Award className="w-4 h-4 text-violet" />
              {t('home.recentBadges')}
            </h3>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="flex gap-3">
            {recentBadges.map((b) => {
              const BadgeIcon = getBadgeIcon(b.slug, b.category);
              return (
                <div key={b.id} className="flex-1 p-3 rounded-xl bg-violet/5 border border-violet/10 text-center">
                  <BadgeIcon className="w-6 h-6 text-violet mx-auto mb-1" />
                  <p className="text-caption font-medium text-foreground leading-tight">
                    {catalogText(b.slug, 'name', b.name)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Logo Collection */}
      {collectionPreview.length > 0 && (
        <div className="mx-4 mt-4">
          <button onClick={() => navigate('/collection')} className="flex items-center justify-between w-full mb-2">
            <h3 className="text-small font-semibold text-foreground flex items-center gap-1.5">
              <Layers className="w-4 h-4 text-primary" />
              {t('home.collection')}
              <span className="text-caption text-muted-foreground font-normal">({collectionPreview.length})</span>
            </h3>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="flex gap-3">
            {collectionPreview.map((item) => (
              <div key={item.id} className="flex-1 p-3 rounded-xl bg-primary/5 border border-primary/10 text-center">
                <LogoAvatar
                  logoUrl={item.logoUrl}
                  tenantName={item.tenantName}
                  size="w-8 h-8"
                  initialsSize="text-xs"
                  className="mx-auto mb-1 bg-primary/10"
                />
                <p className="text-caption font-medium text-foreground leading-tight truncate">{item.tenantName}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Globe Reserve Widget */}
      {scout.globeEnabled !== false && reserveStats && reserveStats.total > 0 && (
        <div className="mx-4 mt-4">
          <button
            onClick={() => {
              window.gtag?.('event', 'globe_widget_tap', {
                species_count: reserveStats.total,
                needs_attention: reserveStats.needsAttention + reserveStats.atRisk,
              });
              navigate('/reserve');
            }}
            className="flex items-center justify-between w-full mb-2"
          >
            <h3 className="text-small font-semibold text-foreground flex items-center gap-1.5">
              <Layers className="w-4 h-4 text-primary" />
              {t('globe:widget.title', { defaultValue: 'Your Reserve' })}
              <span className="text-caption text-muted-foreground font-normal">({reserveStats.total})</span>
            </h3>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="p-3 rounded-xl bg-primary/5 border border-primary/10">
            <div className="flex gap-2 mb-2">
              {reserveAnimals.map((animal) => (
                <div key={animal.slug} className="w-12 h-12 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0">
                  <img
                    src={animal.thumbPath}
                    alt={animal.name}
                    className={`w-full h-full object-cover ${
                      animal.healthState === 'thriving'
                        ? 'opacity-100 saturate-[1.3]'
                        : animal.healthState === 'fading'
                          ? 'opacity-70 saturate-[0.6] brightness-[0.85]'
                          : 'opacity-45 saturate-[0.3] brightness-[0.75] grayscale-[0.3]'
                    }`}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              ))}
            </div>
            {reserveStats.needsAttention > 0 || reserveStats.atRisk > 0 ? (
              <p className="text-caption text-amber-600">
                {t('globe:widget.needsAttention', {
                  count: reserveStats.needsAttention + reserveStats.atRisk,
                  defaultValue: '{{count}} need attention',
                })}
              </p>
            ) : (
              <p className="text-caption text-emerald-600">
                {t('globe:widget.allThriving', { defaultValue: 'All species thriving' })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Getting Started (early scouts) OR Rewards/Invite (established scouts) */}
      {scout.totalScans > 0 && scout.totalScans <= 5 ? (
        <GettingStartedCard scout={scout} t={t} />
      ) : (
        <>
          {/* Rewards CTA */}
          <div className="mx-4 mt-4">
            <button
              onClick={() => navigate('/rewards')}
              className="w-full p-4 rounded-xl bg-gradient-to-r from-gold/5 to-gold/10 border border-gold/20 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                <Gift className="w-5 h-5 text-gold" />
              </div>
              <div className="flex-1 text-start">
                <p className="text-small font-semibold text-foreground">{t('home.rewards')}</p>
                <p className="text-caption text-muted-foreground">{t('home.rewardsHint')}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </>
      )}

      {/* Invite Friends */}
      <div className="mx-4 mt-4">
        <button
          onClick={() => navigate('/referrals')}
          className="w-full p-4 rounded-xl bg-gradient-to-r from-primary/5 to-forest-wash border border-forest/20 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg bg-forest-wash flex items-center justify-center">
            <Users className="w-5 h-5 text-forest" />
          </div>
          <div className="flex-1 text-start">
            <p className="text-small font-semibold text-foreground">{t('home.inviteFriends')}</p>
            <p className="text-caption text-muted-foreground">
              {t('home.inviteFriendsHint', { xp: SCOUT_POINT_VALUES.REFERRAL_BONUS })}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNav pendingScans={pendingCount} />
    </div>
  );
}

function GettingStartedCard({
  scout,
  t,
}: {
  scout: { totalScans: number; totalFeedback: number; totalIssues: number; currentStreak: number };
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const checks = [scout.totalScans >= 1, scout.totalFeedback >= 1, scout.totalScans >= 3, scout.currentStreak >= 3];
  const allDone = checks.every(Boolean);
  const doneCount = checks.filter(Boolean).length;

  return (
    <div className="mx-4 mt-4">
      <div
        className={`rounded-xl p-4 shadow-sm border ${allDone ? 'bg-forest-wash border-forest/20' : 'bg-white border-warm-border'}`}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-small font-semibold text-foreground">
            {allDone ? t('home.checklistComplete') : t('home.gettingStarted')}
          </h3>
          <span className="text-caption text-muted-foreground">{doneCount}/4</span>
        </div>
        {allDone ? (
          <p className="text-small text-forest">{t('home.checklistCompleteHint')}</p>
        ) : (
          <div className="space-y-2.5">
            <ChecklistItem done={scout.totalScans >= 1} label={t('home.checklistScan')} />
            <ChecklistItem done={scout.totalFeedback >= 1} label={t('home.checklistRate')} />
            <ChecklistItem done={scout.totalScans >= 3} label={t('home.checklistThreeScans')} />
            <ChecklistItem done={scout.currentStreak >= 3} label={t('home.checklistStreak')} />
          </div>
        )}
      </div>
    </div>
  );
}

function ChecklistItem({ done, label }: { done: boolean; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-forest' : 'bg-muted'}`}
      >
        {done ? (
          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <div className="w-2 h-2 rounded-full bg-warm-border" />
        )}
      </div>
      <span className={`text-small ${done ? 'text-muted-foreground line-through' : 'text-foreground font-medium'}`}>
        {label}
      </span>
    </div>
  );
}

function BottomNav({ pendingScans = 0 }: { pendingScans?: number }) {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { path: '/', icon: Home, label: t('nav.home') },
    { path: '/missions', icon: Target, label: t('nav.missions') },
    { path: '/scan', icon: Camera, label: t('nav.scan'), primary: true },
    { path: '/reserve', icon: Layers, label: t('nav.reserve', { defaultValue: 'Reserve' }) },
    { path: '/profile', icon: User, label: t('nav.profile') },
  ];

  return (
    <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-warm-border pb-safe z-20">
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto">
        {tabs.map((tab) => {
          const isActive = location.pathname === tab.path;
          const Icon = tab.icon;

          if (tab.primary) {
            return (
              <button
                type="button"
                key={tab.path}
                onClick={() => navigate(tab.path)}
                className="flex flex-col items-center justify-center -mt-5"
              >
                <div className="relative w-14 h-14 rounded-full bg-gradient-to-r from-primary to-forest flex items-center justify-center shadow-lg">
                  <Icon className="w-6 h-6 text-white" />
                  {pendingScans > 0 && (
                    <span className="absolute -top-1 -end-1 flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-gold text-white text-[10px] font-bold">
                      <Cloud className="w-3 h-3 me-0.5" />
                      {pendingScans}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium text-primary mt-0.5">{tab.label}</span>
              </button>
            );
          }

          return (
            <button
              type="button"
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className="flex flex-col items-center justify-center gap-0.5 min-w-[56px]"
            >
              <Icon className={`w-5 h-5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
              <span className={`text-[10px] font-medium ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
